import { checkFieldCondition } from './actionHelper'
import { isChecklistField } from './checklistHelper'
import type { DynamicField } from '../types'
import { toDateOnly } from '@lib/tz'

export const isEmptyValue = (value: unknown) =>
  value === '' || value === null || typeof value === 'undefined' || (Array.isArray(value) && !value.length)

const buildDefault = (field: DynamicField) => {
  if (isChecklistField(field)) return []

  // A `date` default can arrive as a timestamp; an untouched field must still submit a bare date.
  if (field.type === 'date') return toDateOnly(field.default)

  // A multi-selector submits an array; an untouched field must start as one, not ''.
  if (field.type === 'selector' && field.multiple) return []

  return field.default ?? ''
}

export const buildDefaults = (fields?: Array<DynamicField>) =>
  fields?.reduce<Record<string, unknown>>((acc, f) => {
    acc[f.param_key || ''] = buildDefault(f)

    return acc
  }, {})

export const isFieldVisible = (field: DynamicField, values: Record<string, unknown>) => {
  const visibleIf = 'visible_if' in field ? field.visible_if : undefined

  return !visibleIf || checkFieldCondition(values, visibleIf)
}

export const isFieldRequired = (field: DynamicField, values: Record<string, unknown>) => {
  if (typeof field.required === 'boolean') return field.required

  const conditions = field.required ?? {}

  return Object.keys(conditions).length > 0 && checkFieldCondition(values, conditions)
}

export const buildSubmitPayload = (fields: Array<DynamicField> | undefined, values: Record<string, unknown>) => {
  const hiddenKeys = new Set((fields ?? []).filter((f) => !isFieldVisible(f, values)).map((f) => f.param_key || ''))

  return Object.entries(values).reduce<Record<string, unknown>>((acc, [key, value]) => {
    if (!hiddenKeys.has(key) && !isEmptyValue(value)) acc[key] = value

    return acc
  }, {})
}

const isFile = (value: unknown): value is File => typeof File !== 'undefined' && value instanceof File

/**
 * Actions with a `file` upload field must go out as multipart; everything else keeps its JSON body.
 * Rails reads every non-file part as a string, so nested values are serialised before appending.
 */
export const toRequestBody = (payload: Record<string, unknown>) => {
  if (!Object.values(payload).some(isFile)) return payload

  return Object.entries(payload).reduce((form, [key, value]) => {
    if (isFile(value)) form.append(key, value)
    else if (value !== null && typeof value === 'object') form.append(key, JSON.stringify(value))
    else form.append(key, String(value))

    return form
  }, new FormData())
}
